// Google Drive Service for Baba AI
// Handles OAuth authentication and file operations (read/write/organize)

const CLIENT_ID = '675400760719-3f66h4r54269ga6n71aav7hh854je0qk.apps.googleusercontent.com';
const REDIRECT_URI = 'https://baba-aiv15.pages.dev/oauth/callback';
const SCOPES = [
  'https://www.googleapis.com/auth/drive.file',      // Create/edit files created by app
  'https://www.googleapis.com/auth/drive.appdata',   // App-specific data
  'https://www.googleapis.com/auth/drive.readonly'   // Read all files
].join(' ');

const API_BASE = 'https://www.googleapis.com/drive/v3';
const UPLOAD_BASE = 'https://www.googleapis.com/upload/drive/v3';

class GoogleDriveService {
  constructor() {
    this.accessToken = localStorage.getItem('gdrive_access_token');
    this.refreshToken = localStorage.getItem('gdrive_refresh_token');
    this.tokenExpiry = localStorage.getItem('gdrive_token_expiry');
  }

  // ========== AUTHENTICATION ==========

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    if (!this.accessToken) return false;
    if (this.tokenExpiry && Date.now() > parseInt(this.tokenExpiry)) {
      return false; // Token expired
    }
    return true;
  }

  /**
   * Initiate OAuth flow - redirects to Google
   */
  initiateAuth() {
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.set('client_id', CLIENT_ID);
    authUrl.searchParams.set('redirect_uri', REDIRECT_URI);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', SCOPES);
    authUrl.searchParams.set('access_type', 'offline'); // Get refresh token
    authUrl.searchParams.set('prompt', 'consent'); // Force consent to get refresh token
    
    window.location.href = authUrl.toString();
  }

  /**
   * Handle OAuth callback - exchange code for tokens
   * Call this from your callback route
   */
  async handleCallback(code) {
    try {
      // Note: For security, token exchange should happen server-side
      // For now, we'll use a Cloudflare Worker for this
      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          code: code,
          client_id: CLIENT_ID,
          client_secret: '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', // Note: Should be server-side
          redirect_uri: REDIRECT_URI,
          grant_type: 'authorization_code',
        }),
      });

      if (!response.ok) {
        throw new Error('Token exchange failed');
      }

      const tokens = await response.json();
      this.saveTokens(tokens);
      return true;
    } catch (error) {
      console.error('OAuth callback error:', error);
      throw error;
    }
  }

  /**
   * Save tokens to localStorage
   */
  saveTokens(tokens) {
    this.accessToken = tokens.access_token;
    localStorage.setItem('gdrive_access_token', tokens.access_token);
    
    if (tokens.refresh_token) {
      this.refreshToken = tokens.refresh_token;
      localStorage.setItem('gdrive_refresh_token', tokens.refresh_token);
    }
    
    if (tokens.expires_in) {
      const expiry = Date.now() + (tokens.expires_in * 1000);
      this.tokenExpiry = expiry;
      localStorage.setItem('gdrive_token_expiry', expiry.toString());
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshAccessToken() {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        refresh_token: this.refreshToken,
        client_id: CLIENT_ID,
        client_secret: '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',
        grant_type: 'refresh_token',
      }),
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const tokens = await response.json();
    this.saveTokens(tokens);
  }

  /**
   * Logout - clear all tokens
   */
  logout() {
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiry = null;
    localStorage.removeItem('gdrive_access_token');
    localStorage.removeItem('gdrive_refresh_token');
    localStorage.removeItem('gdrive_token_expiry');
  }

  /**
   * Make authenticated API request with auto-refresh
   */
  async apiRequest(url, options = {}) {
    // Check if token needs refresh
    if (this.tokenExpiry && Date.now() > parseInt(this.tokenExpiry) - 60000) {
      await this.refreshAccessToken();
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        ...options.headers,
      },
    });

    // If unauthorized, try to refresh token and retry
    if (response.status === 401) {
      await this.refreshAccessToken();
      return fetch(url, {
        ...options,
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          ...options.headers,
        },
      });
    }

    return response;
  }

  // ========== FILE OPERATIONS ==========

  /**
   * List files in a folder or search
   */
  async listFiles(options = {}) {
    const params = new URLSearchParams({
      pageSize: options.pageSize || '100',
      fields: 'files(id,name,mimeType,modifiedTime,size,parents)',
    });

    if (options.folderId) {
      params.set('q', `'${options.folderId}' in parents and trashed = false`);
    } else if (options.query) {
      params.set('q', options.query);
    } else {
      params.set('q', 'trashed = false');
    }

    if (options.orderBy) {
      params.set('orderBy', options.orderBy);
    }

    const response = await this.apiRequest(`${API_BASE}/files?${params}`);
    
    if (!response.ok) {
      throw new Error(`Failed to list files: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Search for files by name pattern
   */
  async searchFiles(namePattern, mimeType = null) {
    let query = `name contains '${namePattern}' and trashed = false`;
    if (mimeType) {
      query += ` and mimeType = '${mimeType}'`;
    }
    
    return this.listFiles({ query });
  }

  /**
   * Get file metadata
   */
  async getFileMetadata(fileId) {
    const response = await this.apiRequest(
      `${API_BASE}/files/${fileId}?fields=id,name,mimeType,modifiedTime,size,parents`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to get file metadata: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Read file content
   */
  async readFile(fileId) {
    // First get metadata to check mime type
    const metadata = await this.getFileMetadata(fileId);
    
    // For Google Docs, export as plain text or markdown
    if (metadata.mimeType === 'application/vnd.google-apps.document') {
      const response = await this.apiRequest(
        `${API_BASE}/files/${fileId}/export?mimeType=text/plain`
      );
      return response.text();
    }
    
    // For regular files, download content
    const response = await this.apiRequest(
      `${API_BASE}/files/${fileId}?alt=media`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to read file: ${response.statusText}`);
    }

    // Return as text for text files, blob otherwise
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text')) {
      return response.text();
    }
    return response.blob();
  }

  /**
   * Create a new file
   */
  async createFile(name, content, mimeType = 'text/plain', parentFolderId = null) {
    const metadata = {
      name: name,
      mimeType: mimeType,
    };
    
    if (parentFolderId) {
      metadata.parents = [parentFolderId];
    }

    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', new Blob([content], { type: mimeType }));

    const response = await this.apiRequest(
      `${UPLOAD_BASE}/files?uploadType=multipart&fields=id,name,mimeType,modifiedTime`,
      {
        method: 'POST',
        body: form,
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to create file: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update file content
   */
  async updateFile(fileId, content, mimeType = 'text/plain') {
    const response = await this.apiRequest(
      `${UPLOAD_BASE}/files/${fileId}?uploadType=media&fields=id,name,mimeType,modifiedTime`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': mimeType,
        },
        body: content,
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to update file: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Delete a file (move to trash)
   */
  async deleteFile(fileId) {
    const response = await this.apiRequest(
      `${API_BASE}/files/${fileId}`,
      {
        method: 'DELETE',
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to delete file: ${response.statusText}`);
    }

    return true;
  }

  /**
   * Create a folder
   */
  async createFolder(name, parentFolderId = null) {
    const metadata = {
      name: name,
      mimeType: 'application/vnd.google-apps.folder',
    };
    
    if (parentFolderId) {
      metadata.parents = [parentFolderId];
    }

    const response = await this.apiRequest(
      `${API_BASE}/files?fields=id,name,mimeType`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(metadata),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to create folder: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Find or create a folder by name
   */
  async findOrCreateFolder(name, parentFolderId = null) {
    // Search for existing folder
    let query = `name = '${name}' and mimeType = 'application/vnd.google-apps.folder' and trashed = false`;
    if (parentFolderId) {
      query += ` and '${parentFolderId}' in parents`;
    }
    
    const result = await this.listFiles({ query });
    
    if (result.files && result.files.length > 0) {
      return result.files[0]; // Return existing folder
    }
    
    // Create new folder
    return this.createFolder(name, parentFolderId);
  }

  // ========== BABA AI SPECIFIC OPERATIONS ==========

  /**
   * Initialize Baba_Workspace folder structure
   */
  async initializeBabaWorkspace() {
    // Create main workspace folder
    const workspace = await this.findOrCreateFolder('Baba_Workspace');
    
    // Create subfolders
    const folders = [
      'Master_Context',
      'Specialized_Contexts',
      'ChatGPT_Import',
      'Archives',
      'Outputs'
    ];
    
    const createdFolders = { root: workspace };
    
    for (const folderName of folders) {
      const folder = await this.findOrCreateFolder(folderName, workspace.id);
      createdFolders[folderName] = folder;
    }
    
    // Create specialized context subfolders
    const specializedFolder = createdFolders['Specialized_Contexts'];
    const specializedSubfolders = ['CREAM', 'GTR', 'XRP'];
    
    for (const subName of specializedSubfolders) {
      await this.findOrCreateFolder(subName, specializedFolder.id);
    }
    
    console.log('Baba_Workspace initialized:', createdFolders);
    return createdFolders;
  }

  /**
   * Find latest MCS version
   */
  async findLatestMCS() {
    const result = await this.searchFiles('MCS_v');
    
    if (!result.files || result.files.length === 0) {
      return null;
    }
    
    // Sort by version number (extract from filename)
    const sorted = result.files.sort((a, b) => {
      const versionA = this.extractVersion(a.name);
      const versionB = this.extractVersion(b.name);
      return versionB - versionA; // Descending
    });
    
    return sorted[0];
  }

  /**
   * Extract version number from filename (e.g., "MCS_v2_11.md" -> 2.11)
   */
  extractVersion(filename) {
    const match = filename.match(/v(\d+)[_.](\d+)/i);
    if (match) {
      return parseFloat(`${match[1]}.${match[2]}`);
    }
    return 0;
  }

  /**
   * Archive a chat conversation
   */
  async archiveChat(chatData, chatName) {
    // Ensure Archives folder exists
    const workspace = await this.findOrCreateFolder('Baba_Workspace');
    const archives = await this.findOrCreateFolder('Archives', workspace.id);
    
    // Create filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `Chat_${timestamp}_${chatName.replace(/[^a-zA-Z0-9]/g, '_')}.json`;
    
    // Save chat data
    const content = JSON.stringify(chatData, null, 2);
    return this.createFile(filename, content, 'application/json', archives.id);
  }

  /**
   * List archived chats
   */
  async listArchivedChats() {
    const workspace = await this.findOrCreateFolder('Baba_Workspace');
    const archives = await this.findOrCreateFolder('Archives', workspace.id);
    
    return this.listFiles({
      folderId: archives.id,
      orderBy: 'modifiedTime desc'
    });
  }
}

// Export singleton instance
const googleDriveService = new GoogleDriveService();
export default googleDriveService;
